import React from "react";

interface Slide {
	src: string;
	alt: string;
}

/** Page context: which folder to use for slideshow images. No mixing between pages. */
export type ProductSlideshowVariant = "agencies" | "productTeams";

interface ProductSlideshowSectionProps {
	/** Which landing page is rendering: selects image folder. Required so each page gets the correct set. */
	variant: ProductSlideshowVariant;
}

/**
 * Slides for For Agencies page only.
 * Source: public/slide-show/For Agencies/. Order: imiDesktop, imiMobile, Crafted1, ptrDesktop, ptrMobile, Crafted2, zooDesktop, zooMobile, borshoffMobile, boshoffDesktop, Crafted3.
 */
const AGENCIES_SLIDES: Slide[] = [
	{ src: "/slide-show/For Agencies/imiDesktop.jpg", alt: "IMI Desktop" },
	{ src: "/slide-show/For Agencies/imiMobile.jpg", alt: "IMI Mobile" },
	{ src: "/slide-show/For Agencies/Crafted1.jpg", alt: "Crafted Project 1" },
	{ src: "/slide-show/For Agencies/ptrDesktop.jpg", alt: "PTR Desktop" },
	{ src: "/slide-show/For Agencies/ptrMobile.jpg", alt: "PTR Mobile" },
	{ src: "/slide-show/For Agencies/Crafted2.jpg", alt: "Crafted Project 2" },
	{ src: "/slide-show/For Agencies/zooDesktop.jpg", alt: "Zoo Desktop" },
	{ src: "/slide-show/For Agencies/zooMobile.jpg", alt: "Zoo Mobile" },
	{ src: "/slide-show/For Agencies/borshoffMobile.jpg", alt: "Borshoff Mobile" },
	{ src: "/slide-show/For Agencies/boshoffDesktop.jpg", alt: "Borshoff Desktop" },
	{ src: "/slide-show/For Agencies/Crafted3.jpg", alt: "Crafted Project 3" },
];

/**
 * Slides for For Product Teams page only.
 * Source: public/slide-show/For Product Teams/. Order: usafbLeagueFinder, yojo, usafbDashboard, MISOGateway, AllIn, Lensquote, Fastlane, MedRep, MISOLMPContourMap, Nexsyis.
 */
const PRODUCT_TEAMS_SLIDES: Slide[] = [
	{ src: "/slide-show/For Product Teams/usafbLeagueFinder.jpg", alt: "USAFB League Finder" },
	{ src: "/slide-show/For Product Teams/yojo.jpg", alt: "Yojo" },
	{ src: "/slide-show/For Product Teams/usafbDashboard.jpg", alt: "USAFB Dashboard" },
	{ src: "/slide-show/For Product Teams/MISOGateway.jpg", alt: "MISO Gateway" },
	{ src: "/slide-show/For Product Teams/AllIn.jpg", alt: "All In" },
	{ src: "/slide-show/For Product Teams/Lensquote.jpg", alt: "Lensquote" },
	{ src: "/slide-show/For Product Teams/Fastlane.jpg", alt: "Fastlane" },
	{ src: "/slide-show/For Product Teams/MedRep.jpg", alt: "Med Rep" },
	{ src: "/slide-show/For Product Teams/MISOLMPContourMap.jpg", alt: "MISO LMP Contour Map" },
	{ src: "/slide-show/For Product Teams/Nexsyis.jpg", alt: "Nexsyis" },
];

function getSlidesForVariant(variant: ProductSlideshowVariant): Slide[] {
	if (variant === "agencies") {
		return AGENCIES_SLIDES;
	}
	return PRODUCT_TEAMS_SLIDES;
}

const ProductSlideshowSection = ({ variant }: ProductSlideshowSectionProps) => {
	const slidesToUse = getSlidesForVariant(variant);

	return (
		<div className="bg-white overflow-hidden px-2 xl:px-0">
			<div className="relative w-full overflow-hidden">
				<div className="flex animate-scroll w-max hover:[animation-play-state:paused] items-center">
					{/* Tripling for smooth scrolling */}
					{[...slidesToUse, ...slidesToUse, ...slidesToUse].map((slide, index) => (
						/* eslint-disable-next-line @next/next/no-img-element */
						<img
							key={`${slide.src}-${index}`}
							src={slide.src}
							alt={slide.alt}
							className="h-[400px] md:h-[600px] w-auto max-w-none object-contain"
						/>
					))}
				</div>
			</div>
		</div>
	);
};

export default ProductSlideshowSection;
